import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Per-tenant feature flags. One fetch on auth → exposed via context so
// nav items and routes can gate themselves without each page re-fetching.
// Defaults to "everything disabled" while loading so we never flash UI
// the tenant isn't entitled to see.

const DEFAULTS = {
  ai_features_enabled: false,
  enable_group_messaging: false,
  enable_text_blasts: false,
  enable_integrations: false,
  international_enabled: false,
  message_footer_enabled: false,
  rbm_enabled: false,
  // rbm_live = rbm_enabled AND rbm_sender_id set. This is what the
  // send path and the attach-size cap gate on; rbm_enabled alone can
  // be true while Bandwidth setup is still in progress.
  rbm_live: false,
};

const TenantFeaturesContext = createContext({ features: DEFAULTS, loading: true, refresh: () => {} });

export function TenantFeaturesProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [features, setFeatures] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Don't declare "loaded" while AuthContext is still resolving the
    // session. Previously we set loading=false immediately with DEFAULTS,
    // which meant FeatureGate briefly evaluated on empty flags and
    // redirected feature-gated routes to /. That silently ate OAuth
    // callback redirects (Teams/Clio/Salesforce) — the ?code=... query
    // never reached the target page.
    if (authLoading) return;
    if (!user) {
      setFeatures(DEFAULTS);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/settings/features');
      setFeatures({ ...DEFAULTS, ...res.data });
    } catch (e) {
      // Endpoint may 404 for users without a tenant (shouldn't happen for
      // logged-in users post-2026-05-28, but be defensive). Keep defaults.
      setFeatures(DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <TenantFeaturesContext.Provider value={{ features, loading, refresh }}>
      {children}
    </TenantFeaturesContext.Provider>
  );
}

export function useTenantFeatures() {
  return useContext(TenantFeaturesContext);
}
